import * as PIXI from "pixi.js";
import {config} from "../../config";
import {BetPanelTextField} from "../BetPanelTextField";
import data from "../configs/data";
import {Button} from "../controlElements/Button";
import {Dice} from "../Dice";
import {TextInput} from "../PIXI.TextInput";

export class BetPanel extends PIXI.Container {

    private readonly BET_VALUE_FIELD_POSITION = new PIXI.Point(-256, 55);
    private readonly BET_VALUE_INPUT_POSITION = new PIXI.Point(-383, 19);
    private readonly REWARD_TEXT_FIELD_POSITION = new PIXI.Point(0, 55);
    private readonly BALANCE_TEXT_FIELD_POSITION = new PIXI.Point(256, 55);
    private readonly DEFAULT_ANCHOR_POINT: any = new PIXI.Point(0.5, 0.5);
    private readonly HALF_BET_BUTTON_POSITION: any = new PIXI.Point(-160, 128);
    private readonly DOUBLE_BET_BUTTON_POSITION: any = new PIXI.Point(0, 128);
    private readonly MAX_BET_BUTTON_POSITION: any = new PIXI.Point(160, 128);
    private readonly DEFAULT_BET_VALUE = "1.000";

    private readonly BET_OPTION_TEXT_STYLE = {
        fontSize: 19,
        fontFamily: "RobotoRegular",
        fontWeight: "bolder",
        fill: "#ffffff",
    };

    private betValueInput: TextInput;
    private betValueField: BetPanelTextField;
    private rewardField: BetPanelTextField;
    private balanceField: BetPanelTextField;
    private payoutFactor: number = 2;

    constructor() {
        super();

        this.createAndAddHalfBetButton();
        this.createAndAddDoubleBetButton();
        this.createAndAddMaxBetButton();

        this.createAndAddBetValueTextField();
        this.createAndAddRewardTextField();
        this.createAndAddBalanceTextField();
        this.createAndAddBetValueInput();
        this.refreshRewardValue();
    }

    public setBalanceValue(value: number): void {
        this.balanceField.setValue(value.toFixed(config.betDisplayPrecision), config.baseCurrency);
    }

    public getBetAmount(): number {
        return parseFloat(this.betValueInput.text);
    }

    public setPayoutFactor(value: number): void {
        this.payoutFactor = value;
        this.refreshRewardValue();

    }

    private createAndAddBetValueInput(): void {
        this.betValueInput = new TextInput({
            input: {
                fontSize: "25pt",
                padding: "13px",
                width: "256px",
                color: "#ffffff",
            },
            box: {
                default: {fill: 0x000, stroke: {color: 0x08d5ee, width: 1}},
                focused: {fill: 0x000, stroke: {color: 0x08d5ee, width: 3}},
                disabled: {fill: 0x000},
            },
        });
        this.betValueInput.position = this.BET_VALUE_INPUT_POSITION;
        this.betValueInput.htmlInput.addEventListener("keypress", (evt) => {
            const charCode = (evt.which) ? evt.which : evt.keyCode;
            if ((this.betValueInput.htmlInput.value.includes(".") && charCode === 46)) {
                return false;
            }
            return !(charCode > 31 && (charCode !== 46 && (charCode < 48 || charCode > 57)));
        });
        this.betValueInput.htmlInput.addEventListener("change", () => {
            let valueNumber = parseFloat(this.betValueInput.htmlInput.value);
            if (valueNumber > config.maxBet) {
                valueNumber = config.maxBet;
            }
            if (config.maxWinAllowed < this.payoutFactor * valueNumber) {
                valueNumber = config.maxWinAllowed / this.payoutFactor;
            }
            if (valueNumber < config.minBet) {
                valueNumber = config.minBet;
            }

            this.betValueInput.htmlInput.value = valueNumber.toFixed(config.betDisplayPrecision);
            this.refreshRewardValue();
        });
        this.betValueInput.htmlInput.value = this.DEFAULT_BET_VALUE;
        this.addChild(this.betValueInput);
    }

    private createAndAddHalfBetButton(): void {
        const button = new Button(data.images.betOptionBase, data.images.betOptionHover,
            data.images.betOptionClick, data.images.betOptionInActive, data.audios.buttonClicked);
        button.position = this.HALF_BET_BUTTON_POSITION;
        button.scale = Dice.ELEMENTS_SCALE;
        button.label = new PIXI.Text("1/2", this.BET_OPTION_TEXT_STYLE);
        button.label.anchor = this.DEFAULT_ANCHOR_POINT;
        button.subscribeOnClicked(() => {
            let newValue = parseFloat(this.betValueInput.htmlInput.value) / 2;
            if (newValue < config.minBet) {
                newValue = config.minBet;
            }
            this.betValueInput.htmlInput.value = newValue.toFixed(config.betDisplayPrecision);
            this.betValueInput.text = newValue.toFixed(config.betDisplayPrecision);
            this.refreshRewardValue();
        });
        this.addChild(button);
    }

    private createAndAddDoubleBetButton(): void {
        const button = new Button(data.images.betOptionBase, data.images.betOptionHover,
            data.images.betOptionClick, data.images.betOptionInActive, data.audios.buttonClicked);
        button.position = this.DOUBLE_BET_BUTTON_POSITION;
        button.scale = Dice.ELEMENTS_SCALE;
        button.label = new PIXI.Text("x2", this.BET_OPTION_TEXT_STYLE);
        button.label.anchor = this.DEFAULT_ANCHOR_POINT;
        button.subscribeOnClicked(() => {
            let newValue = parseFloat(this.betValueInput.htmlInput.value) * 2;
            if (newValue > config.maxBet) {
                newValue = config.maxBet;
            }
            if (config.maxWinAllowed < this.payoutFactor * newValue) {
                newValue = config.maxWinAllowed / this.payoutFactor;
            }
            this.betValueInput.htmlInput.value = newValue.toFixed(config.betDisplayPrecision);
            this.betValueInput.text = newValue.toFixed(config.betDisplayPrecision);
            this.refreshRewardValue();
        });
        this.addChild(button);
    }

    private createAndAddMaxBetButton(): void {
        const button = new Button(data.images.betOptionBase, data.images.betOptionHover,
            data.images.betOptionClick, data.images.betOptionInActive, data.audios.buttonClicked);
        button.position = this.MAX_BET_BUTTON_POSITION;
        button.scale = Dice.ELEMENTS_SCALE;
        button.label = new PIXI.Text("MAX", this.BET_OPTION_TEXT_STYLE);
        button.label.anchor = this.DEFAULT_ANCHOR_POINT;
        button.subscribeOnClicked(() => {
            let newValue = config.maxBet;
            if (config.maxWinAllowed < this.payoutFactor * newValue) {
                newValue = config.maxWinAllowed / this.payoutFactor;
            }
            this.betValueInput.htmlInput.value = newValue.toFixed(config.betDisplayPrecision);
            this.betValueInput.text = newValue.toFixed(config.betDisplayPrecision);
            this.refreshRewardValue();
        });
        this.addChild(button);
    }

    private createAndAddBetValueTextField(): void {
        this.betValueField = new BetPanelTextField("BET VALUE");
        this.betValueField.setValue("", config.baseCurrency);
        this.betValueField.position = this.BET_VALUE_FIELD_POSITION;
        this.addChild(this.betValueField);
    }

    private createAndAddRewardTextField(): void {
        this.rewardField = new BetPanelTextField("REWARD");
        this.rewardField.position = this.REWARD_TEXT_FIELD_POSITION;
        this.addChild(this.rewardField);
    }

    private createAndAddBalanceTextField(): void {
        this.balanceField = new BetPanelTextField("BALANCE");
        this.balanceField.position = this.BALANCE_TEXT_FIELD_POSITION;
        this.addChild(this.balanceField);
    }

    private refreshRewardValue(): void {
        const oldValue = parseFloat(this.betValueInput.htmlInput.value);
        let newValue = oldValue;
        if (newValue > config.maxBet) {
            newValue = config.maxBet;
        }
        if (config.maxWinAllowed < this.payoutFactor * newValue) {
            newValue = config.maxWinAllowed / this.payoutFactor;
        }
        if (oldValue > newValue) {
            this.betValueInput.htmlInput.value = newValue.toFixed(config.betDisplayPrecision);
            this.betValueInput.text = newValue.toFixed(config.betDisplayPrecision);
        }

        this.rewardField.setValue(
            (this.payoutFactor * parseFloat(this.betValueInput.text)).toFixed(config.betDisplayPrecision + 1), config.baseCurrency);
    }
}



// WEBPACK FOOTER //
// ./src/app/userInterface/BetPanel.ts